import { AxiosError, AxiosResponse } from 'axios';
import qs from 'qs-stringify';
import * as Sentry from '@sentry/react';

import { RESPONSE_CODES } from '../constants/responseCodes.constants';
import { PaginationFactory, PaginatorFactory } from '../factory';
import { api, isRequestCanceled } from '../http';
import { store } from '../store';
import { SystemUserTypes, DefaultCallback, DefaultRes } from '../types';
import { CustomerTypes } from '../types/customer.types';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { UserService } from './user.service';
import { parseCcEmails } from '../helpers/parseCcEmails';
import { handleApiError } from './react-query/react-query.client';
import { startAuthenticatedReplay } from './sentry-replay.service';

const customerUriString = 'customer';
export class CustomerService {
    public static readonly dispatch = store.dispatch.customer;
    public static readonly adminDispatch = store.dispatch.customersAdmin;
    public static readonly authDispath = store.dispatch.auth;
    public static readonly state = store.getState();

    public static async fetchMany(
        dto: PaginatorFactory<CustomerTypes.Filters>,
        callback?: DefaultCallback
    ): Promise<PaginationFactory<CustomerTypes.Model> | void> {
        const { adminDispatch } = CustomerService;

        try {
            adminDispatch.toggleLoading('fetchMany', true);

            // qsfy is the query string
            const qsfy = qs({
                perPage: dto.perPage,
                page: dto?.page,
                filter: {
                    ...dto?.filter,
                },
            });
            const {
                data: {
                    result: { items, page, perPage, total },
                    isValid,
                    message,
                },
            } = await api.get<
                DefaultRes<PaginationFactory<CustomerTypes.Model>>,
                AxiosResponse<
                    DefaultRes<PaginationFactory<CustomerTypes.Model>>
                >
                // TODO: Check that GET customer is only allowed for POM Admin
            >(`/${customerUriString}/getMany?${qsfy}`);

            adminDispatch.toggleLoading('fetchMany', false);

            if (isValid) {
                adminDispatch.setPagination({ items, page, perPage, total });
            }

            callback && callback(isValid, RESPONSE_CODES[message]);

            return { items, page, perPage, total };
        } catch (error: any) {
            // toggleLoading to false to stop the loading animation
            adminDispatch.toggleLoading('fetchMany', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<CustomerTypes.Model>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
            }
            callback && callback(false, String(error));
        }
    }

    public static async create(
        customerPayload: CustomerTypes.CreateCustomerParams,
        callback: DefaultCallback
    ) {
        const { adminDispatch } = CustomerService;
        // Comma-separated form field → the array the API validates.
        const ccEmails = parseCcEmails(customerPayload.ccEmails);
        try {
            adminDispatch.toggleLoading('create', true);

            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<CustomerTypes.CreateCustomerResponse>,
                AxiosResponse<DefaultRes<CustomerTypes.CreateCustomerResponse>>
            >('/customer', {
                ...customerPayload,
                ccEmails: ccEmails.length ? ccEmails : undefined,
                subscription: {
                    // Standalone orgs get zero licenses even if an older API
                    // build strips the (unknown-to-it) organizationType field.
                    licenseCount: customerPayload.organizationType === 'iraStandalone'
                        ? 0
                        : customerPayload.licenseCount,
                    expiresAt: customerPayload.expiresAt,
                },
            });

            adminDispatch.toggleLoading('create', false);

            if (isValid) {
                adminDispatch.addPaginationItem(result.customer);
                return callback(
                    true,
                    'An invitation was sent successfully to the customer'
                );
            }

            return callback(false, RESPONSE_CODES[message]);
        } catch (error: any) {
            adminDispatch.toggleLoading('create', false);
            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<CustomerTypes.CreateCustomerResponse>
                >).response?.data?.message;
                
                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
            }
            return callback && callback(false, String(error));
        }
    }

    public static async finishOnboarding(
        model: CustomerTypes.OnboardingForm,
        callback: DefaultCallback
    ) {
        const {
            dispatch: { toggleLoading },
        } = CustomerService;
        const {
            authDispath: { auth },
        } = CustomerService;

        try {
            toggleLoading('finishOnboarding', true);

            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<{ user: SystemUserTypes.Model; token: string }>,
                AxiosResponse<
                    DefaultRes<{ user: SystemUserTypes.Model; token: string }>
                >
            >('/customer/onboarding/finish', model);

            toggleLoading('finishOnboarding', false);

            if (isValid) {
                auth(result?.user, result?.token);
                Sentry.setUser({ id: result?.user?.id, email: result?.user?.email, username: result?.user?.fullname });
                startAuthenticatedReplay();
            }

            return (
                callback &&
                callback(
                    isValid,
                    !isValid
                        ? RESPONSE_CODES[message]
                        : 'Successfully registered, enjoy!'
                )
            );
        } catch (error: any) {
            toggleLoading('finishOnboarding', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<
                        DefaultRes<CustomerTypes.CreateCustomerResponse>
                    >
                ).response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
            }
            return callback && callback(false, String(error));
        }
    }

    public static async getCustomerDetails(id?: string, signal?: AbortSignal) {
        const {
            dispatch: { toggleLoading, setDetails },
        } = CustomerService;

        try {
            toggleLoading('getDetails', true);

            const url = id ? `/customer/details?id=${id}` : '/customer/details';

            const res = await api.get<
                DefaultRes<CustomerTypes.GetCustomerDetailsResponse>,
                AxiosResponse<DefaultRes<CustomerTypes.GetCustomerDetailsResponse>>
            >(url, { signal }); 

            if (!res.data.isValid) {
                toast(
                    RESPONSE_CODES[res.data.message!] || RESPONSE_CODES.UNKNOWN, 
                    {
                        autoClose: 1500,
                        type: 'error',
                    }
                );

                toggleLoading('getDetails', false);

                return;
            }

            toggleLoading('getDetails', false);

            setDetails(res.data.result);
            
            return res.data.result;
        } catch (error: any) {
            if (isRequestCanceled(error)) {
                toggleLoading('getDetails', false);
                return;
            }

            toggleLoading('getDetails', false);
            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<CustomerTypes.GetCustomerDetailsResponse>
                >).response?.data?.message;

                toast(
                    RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN, 
                    {
                        autoClose: 1500,
                        type: 'error',
                    }
                );
            }
        }
    }

    public static async resetSubscriptionInviteCode(id: string) {
        const URI = '/customer/changeInviteCode/';
        const {
            dispatch: { toggleLoading, onChangeInviteCode },
        } = CustomerService;

        toggleLoading('changeInviteCode', true);

        try {
            const res = await api.post<DefaultRes<string>, AxiosResponse<DefaultRes<string>>>(URI, { subscriptionId: id });

            if  (res.data.isValid) {
                onChangeInviteCode({ inviteCode: res.data.result, subscriptionId: id });
                toast(
                    'Success resetting invite code, new code is: ' +
                        res.data?.result,
                    {
                        autoClose: 2000,
                        type: 'success',
                    }
                );
            }

            toggleLoading('changeInviteCode', false);
            return res.data.result;
        } catch (error: any) {
            toggleLoading('changeInviteCode', false);
            if (error?.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<string>
                >).response?.data?.message;

                toast(
                    RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN, 
                    {
                        autoClose: 1500,
                        type: 'error',
                    },
                );
            }
        }
    }

    /**
     * POM Master only. Sets the mobile-app license cap on a subscription
     * (ClickUp 868m4vzj7). Resolves to the API result on success and null on
     * failure; toasts are raised here, mirroring resetSubscriptionInviteCode.
     */
    public static async changeLicenseCount(
        subscriptionId: string,
        licenseCount: number,
    ): Promise<CustomerTypes.ChangeLicenseCountResult | null> {
        const {
            dispatch: { toggleLoading, onChangeLicenseCount },
        } = CustomerService;

        toggleLoading('changeLicenseCount', true);

        try {
            const res = await api.post<
                DefaultRes<CustomerTypes.ChangeLicenseCountResult>,
                AxiosResponse<DefaultRes<CustomerTypes.ChangeLicenseCountResult>>
            >('/customer/changeLicenseCount', { subscriptionId, licenseCount });

            if (!res.data.isValid) {
                toast(RESPONSE_CODES[res.data.message] || RESPONSE_CODES.UNKNOWN, {
                    autoClose: 1500,
                    type: 'error',
                });
                return null;
            }

            onChangeLicenseCount({ subscriptionId, licenseCount: res.data.result.licenseCount });
            toast(`License count updated to ${res.data.result.licenseCount}.`, {
                autoClose: 2000,
                type: 'success',
            });

            return res.data.result;
        } catch (error: any) {
            toast(handleApiError(error), { autoClose: 1500, type: 'error' });
            return null;
        } finally {
            toggleLoading('changeLicenseCount', false);
        }
    }

    public static async inviteUserToSubscription(email: string, subscriptionId: string) {
        const {
            dispatch: { toggleLoading },
        } = CustomerService;
        toggleLoading('inviteUserToSubscription', true);

        try {
            const res = await api.post<DefaultRes<boolean>, AxiosResponse<DefaultRes<boolean>>>('/customer/sendUserInviteCode', { subscriptionId, email });

            toggleLoading('inviteUserToSubscription', false);

            if (!res.data.isValid) {
                toast(
                    RESPONSE_CODES[res.data.message] || RESPONSE_CODES.UNKNOWN, 
                    {
                        autoClose: 1500,
                        type: 'error',
                    },
                );
                return;
            }

            toast(
                `Invite successfully sent to: ${email}`,
                {
                    autoClose: 1500,
                    type: 'success',
                },
            );

        } catch (error: any) {
            toggleLoading('inviteUserToSubscription', false);
            if (error?.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<string>
                >).response?.data?.message;

                toast(
                    RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN, 
                    {
                        autoClose: 1500,
                        type: 'error',
                    },
                );
            }
        }
    }

    public static async removeUserFromSubscription(userId: string, subscriptionId: string) {
        const {
            dispatch: { toggleLoading },
        } = CustomerService;

        toggleLoading('removeUserFromSubscription', true);

        try {

            const res = await api.post<DefaultRes<boolean>, AxiosResponse<DefaultRes<boolean>>>('/customer/removeUserFromSubscription', { subscriptionId, userId });

            toggleLoading('removeUserFromSubscription', false);

            if (!res.data.isValid) {
                toast(
                    RESPONSE_CODES[res.data.message] || RESPONSE_CODES.UNKNOWN, 
                    {
                        autoClose: 1500,
                        type: 'error',
                    },
                );
                return false;
            }

            UserService.dispatch.removePaginationItem(userId);

            toast('Successfully removed the user from subscription.', { autoClose: 1000, type: 'success' });

            return true;

        } catch (error: any) {
            toggleLoading('removeUserFromSubscription', false);
            if (error?.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<string>
                >).response?.data?.message;

                toast(
                    RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN, 
                    {
                        autoClose: 1500,
                        type: 'error',
                    },
                );
            }
        }
    }

    public static async changePassword(currentPassword: string, newPassword: string, callback: DefaultCallback) {
        try {
            const { data: { isValid, message }} = await api.post<
                DefaultRes<boolean>,
                AxiosResponse<DefaultRes<boolean>>
            >(`/customerWebAppUser/changePassword`, { currentPassword, newPassword });

            return callback && callback(isValid, RESPONSE_CODES[message]);
        }  catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<boolean>
                >).response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }
}
